/* GrowthOS — Campaign detail "Leads" / "Contacts" member tabs.
   One reusable themed list (the two member tabs are structurally identical: avatar +
   name + a meta line + chevron → open the record), composed entirely of the existing
   `gos-oppr-*` / `gos-bulk-ava` classes already used by AccountContactsTab (no new
   CSS). Wired to the real campaign-member lists the parent (viewCampaigns) fetches
   via apiServices.getLeadsByCampaignId / getContactsByCampaignId — passed down as
   `items` (+ `loading`) so the tab-bar count badge and this card share one API call,
   same pattern as the Account Contacts tab. These replace the legacy unstyled
   ChildLead / ChildContact AG-grids on this page only (those files are untouched,
   still used elsewhere). "Add" navigates to the real add form (blank — neither add
   form supports a campaign prefill to wire into, same as the design's buttons). */
import { useNavigate } from 'react-router-dom';

const ICONS = {
  leads: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" /></>),
  contacts: (<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13A4 4 0 0 1 16 11" /></>),
  plus: <path d="M12 5v14M5 12h14" />,
  briefcase: <path d="M3 7h18v13H3zM8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />,
  building: <path d="M3 21h18M6 21V4h9v17M9 8h3M9 12h3M9 16h3M15 21V9h4v12" />,
  mail: <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7" />,
  chevR: <path d="M9 18l6-6-6-6" />,
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name] || null}
  </svg>
);

const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '').trim().split(/\s+/).slice(0, 2).map((w) => w[0] || '').join('').toUpperCase() || '?';
const avaColor = (name) => AVA_COLORS[String(name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length];

const CONFIG = {
  lead: {
    icon: 'leads',
    title: 'Leads',
    addLabel: 'Add Lead',
    addRoute: '/leads/addLead',
    viewRoute: (id) => `/leads/viewLead?id=${id}`,
    emptyText: 'Leads enrolled in this campaign will appear here.',
    // leads carry a company + email; title is not a lead field
    metaPrimary: (r) => ({ icon: 'building', text: r.company || r.companyName }),
  },
  contact: {
    icon: 'contacts',
    title: 'Contacts',
    addLabel: 'Add Contact',
    addRoute: '/contacts/addContact',
    viewRoute: (id) => `/contacts/viewContact?id=${id}`,
    emptyText: 'Contacts enrolled in this campaign will appear here.',
    metaPrimary: (r) => ({ icon: 'briefcase', text: r.title }),
  },
};

const CampaignMembersTab = ({ kind, items = [], loading = false, canAdd = true }) => {
  const navigate = useNavigate();
  const cfg = CONFIG[kind] || CONFIG.lead;

  return (
    <div className="gos-oppr-card">
      <div className="gos-oppr-head">
        <div className="gos-oppr-title">
          <Ic name={cfg.icon} size={16} />
          {cfg.title}
          <span className="gos-oppr-count">{items.length}</span>
        </div>
        {canAdd ? (
          <button type="button" className="gos-oppr-btn soft" onClick={() => navigate(cfg.addRoute)}>
            <Ic name="plus" size={13} />
            {cfg.addLabel}
          </button>
        ) : null}
      </div>

      {loading ? (
        <div className="gos-oppr-empty">
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        </div>
      ) : items.length === 0 ? (
        <div className="gos-oppr-empty">
          <div className="gos-oppr-empty-ic">
            <Ic name={cfg.icon} size={21} />
          </div>
          <div className="gos-oppr-empty-t">No {cfg.title.toLowerCase()}</div>
          <div className="gos-oppr-empty-s">{cfg.emptyText}</div>
        </div>
      ) : (
        <div className="gos-oppr-list">
          {items.map((r, i) => {
            const name = r.fullName || [r.firstName, r.middleName, r.lastName].filter(Boolean).join(' ').trim() || r.name || '(no name)';
            const primary = cfg.metaPrimary(r);
            const email = r.email || r.emailAddress;
            return (
              <div
                key={r.id ?? i}
                className={'gos-oppr-row' + (i === items.length - 1 ? ' last' : '')}
                style={{ cursor: r.id ? 'pointer' : 'default' }}
                onClick={() => r.id && navigate(cfg.viewRoute(r.id), { state: r })}
              >
                <span className="gos-bulk-ava" style={{ background: avaColor(name) }}>{initials(name)}</span>
                <div className="gos-oppr-main">
                  <div className="gos-oppr-name">{name}</div>
                  <div className="gos-oppr-meta">
                    {primary.text ? (
                      <>
                        <Ic name={primary.icon} size={12} />
                        <span>{primary.text}</span>
                      </>
                    ) : null}
                    {email ? (
                      <>
                        {primary.text ? <span className="gos-oppr-dot">·</span> : null}
                        <Ic name="mail" size={12} />
                        <span>{email}</span>
                      </>
                    ) : null}
                  </div>
                </div>
                <Ic name="chevR" size={15} style={{ color: 'var(--gos-text-3)', flexShrink: 0 }} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CampaignMembersTab;
